import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiCheckCircle,
  FiClock,
  FiFileText,
  FiRefreshCw,
  FiShield,
} from 'react-icons/fi';
import { getLocale, t, useI18n } from '../../i18n';
import {
  privacyRequestService,
  type AccountDeletionReview,
} from '../../services/privacyRequestService';
import { sanitizeText } from '../../utils/sanitize';
import './PrivacyRequestsPage.css';

interface ReviewDraft {
  legalBasis: string;
  retainUntil: string;
  note: string;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return t('Not recorded');
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return t('Not recorded');
  return parsed.toLocaleDateString(getLocale(), {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function toDateInput(value: string | null | undefined): string {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
}

function defaultDraft(review: AccountDeletionReview): ReviewDraft {
  const defaultDates = review.items
    .map((item) => item.default_retain_until)
    .filter((value): value is string => Boolean(value))
    .sort();
  const latestDefault = defaultDates[defaultDates.length - 1];

  return {
    legalBasis: review.items
      .map((item) => item.default_legal_basis)
      .filter(Boolean)
      .join(' '),
    retainUntil: toDateInput(latestDefault),
    note: '',
  };
}

function categoryLabel(category: string): string {
  const labels: Record<string, string> = {
    working_time_and_payroll: 'Working time and payroll',
    incident_and_safety: 'Incident and safety',
    licence_compliance: 'Licence compliance',
    shift_bounded_location: 'Shift-bounded location',
  };
  return t(labels[category] ?? category);
}

function retentionDeadlineReached(review: AccountDeletionReview, now = Date.now()): boolean {
  return review.review_status === 'retained' && review.items.some((item) => (
    item.retain_until && new Date(item.retain_until).getTime() <= now
  ));
}

export default function PrivacyRequestsPage() {
  useI18n();
  const [reviews, setReviews] = useState<AccountDeletionReview[]>([]);
  const [drafts, setDrafts] = useState<Record<string, ReviewDraft>>({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadReviews = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const next = await privacyRequestService.listAccountDeletionReviews();
      setReviews(next);
      setDrafts((current) => {
        const updated = { ...current };
        for (const review of next) {
          updated[review.id] ??= defaultDraft(review);
        }
        return updated;
      });
    } catch (loadError) {
      setError((loadError as Error).message || t('Failed to load privacy reviews'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadReviews();
  }, [loadReviews]);

  const summary = useMemo(() => {
    const now = Date.now();
    const pending = reviews.filter((review) => review.review_status === 'pending');
    return {
      pending: pending.length,
      overdue: reviews.filter((review) => (
        (review.review_status === 'pending'
          && review.review_due_at
          && new Date(review.review_due_at).getTime() < now)
        || retentionDeadlineReached(review, now)
      )).length,
      reviewed: reviews.filter((review) => review.review_status === 'retained').length,
    };
  }, [reviews]);

  const updateDraft = (id: string, patch: Partial<ReviewDraft>) => {
    setDrafts((current) => ({
      ...current,
      [id]: { ...(current[id] ?? { legalBasis: '', retainUntil: '', note: '' }), ...patch },
    }));
  };

  const submitReview = async (review: AccountDeletionReview) => {
    const draft = drafts[review.id] ?? defaultDraft(review);
    const legalBasis = sanitizeText(draft.legalBasis, 2000);
    const note = sanitizeText(draft.note, 1000);

    if (legalBasis.length < 20) {
      setError(t('Record a specific lawful reason for retaining these records.'));
      return;
    }
    if (!draft.retainUntil || new Date(`${draft.retainUntil}T23:59:59.999`).getTime() <= Date.now()) {
      setError(t('Choose a future deletion or review date.'));
      return;
    }

    setSavingId(review.id);
    setError('');
    setSuccess('');
    try {
      await privacyRequestService.recordRetentionReview({
        requestId: review.id,
        legalBasis,
        retainUntil: draft.retainUntil,
        note,
      });
      setSuccess(t('Retention review recorded for {reference}.', { reference: review.reference }));
      await loadReviews();
    } catch (saveError) {
      setError((saveError as Error).message || t('Failed to record retention review'));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="privacy-requests-page">
      <header className="privacy-requests-page__header">
        <div>
          <span className="privacy-requests-page__eyebrow">{t('Privacy operations')}</span>
          <h1>{t('Account deletion reviews')}</h1>
          <p>
            {t('The login and direct personal account data shown here have already been deleted. Review only the employer-controlled work records that remain, document a lawful basis, and set a firm end date.')}
          </p>
        </div>
        <button
          type="button"
          className="btn btn--secondary"
          onClick={() => { void loadReviews(); }}
          disabled={loading}
        >
          <FiRefreshCw aria-hidden="true" />
          {t('Refresh')}
        </button>
      </header>

      {(error || success) && (
        <div className="privacy-requests-page__messages">
          {error && <div className="msg--error">{error}</div>}
          {success && <div className="msg--success">{success}</div>}
        </div>
      )}

      <section className="privacy-requests-page__stats" aria-label={t('Privacy review summary')}>
        <article>
          <FiClock aria-hidden="true" />
          <span>{t('Pending review')}</span>
          <strong>{summary.pending}</strong>
        </article>
        <article className={summary.overdue > 0 ? 'privacy-requests-page__stat--danger' : ''}>
          <FiFileText aria-hidden="true" />
          <span>{t('Overdue')}</span>
          <strong>{summary.overdue}</strong>
        </article>
        <article>
          <FiCheckCircle aria-hidden="true" />
          <span>{t('Reviewed')}</span>
          <strong>{summary.reviewed}</strong>
        </article>
      </section>

      <div className="privacy-requests-page__notice">
        <FiShield aria-hidden="true" />
        <p>
          {t('Do not retain records merely because they may be useful. GDPR requires a specific lawful purpose, data minimisation, restricted access, and deletion when that purpose ends. The suggested dates are platform defaults; your organisation remains responsible for verifying the applicable law and any legal hold.')}
        </p>
      </div>

      {loading ? (
        <div className="privacy-requests-page__state">{t('Loading privacy reviews...')}</div>
      ) : reviews.length === 0 ? (
        <div className="privacy-requests-page__state">
          <FiCheckCircle aria-hidden="true" />
          {t('No account deletion reviews require attention.')}
        </div>
      ) : (
        <div className="privacy-requests-page__list">
          {reviews.map((review) => {
            const draft = drafts[review.id] ?? defaultDraft(review);
            const pending = review.review_status === 'pending';
            const overdue = pending
              && Boolean(review.review_due_at)
              && new Date(review.review_due_at!).getTime() < Date.now();
            const retentionDue = retentionDeadlineReached(review);

            return (
              <article key={review.id} className="privacy-review-card">
                <div className="privacy-review-card__top">
                  <div>
                    <span className="privacy-review-card__reference">
                      {t('Reference')} {review.reference}
                    </span>
                    <h2>{pending ? t('Controller review required') : t('Retention decision recorded')}</h2>
                  </div>
                  <span className={`status-chip ${
                    pending || retentionDue
                      ? overdue || retentionDue ? 'status-chip--danger' : 'status-chip--warning'
                      : 'status-chip--success'
                  }`}>
                    {pending
                      ? overdue ? t('Overdue') : t('Pending')
                      : retentionDue ? t('Retention deadline reached') : t('Reviewed')}
                  </span>
                </div>

                <dl className="privacy-review-card__dates">
                  <div>
                    <dt>{t('Account deleted')}</dt>
                    <dd>{formatDate(review.completed_at)}</dd>
                  </div>
                  <div>
                    <dt>{t('Review due')}</dt>
                    <dd>{formatDate(review.review_due_at)}</dd>
                  </div>
                  {review.reviewed_at && (
                    <div>
                      <dt>{t('Reviewed')}</dt>
                      <dd>{formatDate(review.reviewed_at)}</dd>
                    </div>
                  )}
                </dl>

                <div className="privacy-review-card__categories">
                  {review.items.map((item) => (
                    <div key={item.id} className="privacy-review-card__category">
                      <strong>{categoryLabel(item.category)}</strong>
                      <span>{t('{count} linked records', { count: item.record_count })}</span>
                      <small>
                        {t('Suggested latest review/deletion date')}: {formatDate(item.default_retain_until)}
                      </small>
                    </div>
                  ))}
                </div>

                {pending ? (
                  <div className="privacy-review-card__form">
                    <label className="form__group">
                      <span className="form__label">{t('Documented lawful basis and necessity')}</span>
                      <textarea
                        className="form__input privacy-review-card__textarea"
                        value={draft.legalBasis}
                        onChange={(event) => updateDraft(review.id, { legalBasis: event.target.value })}
                        maxLength={2000}
                      />
                    </label>
                    <label className="form__group">
                      <span className="form__label">{t('Delete or review again by')}</span>
                      <input
                        type="date"
                        className="form__input"
                        value={draft.retainUntil}
                        onChange={(event) => updateDraft(review.id, { retainUntil: event.target.value })}
                      />
                    </label>
                    <label className="form__group">
                      <span className="form__label">{t('Internal note (optional)')}</span>
                      <textarea
                        className="form__input privacy-review-card__textarea privacy-review-card__textarea--short"
                        value={draft.note}
                        onChange={(event) => updateDraft(review.id, { note: event.target.value })}
                        maxLength={1000}
                      />
                    </label>
                    <button
                      type="button"
                      className="btn btn--primary"
                      onClick={() => { void submitReview(review); }}
                      disabled={savingId === review.id}
                    >
                      <FiCheckCircle aria-hidden="true" />
                      {savingId === review.id ? t('Saving...') : t('Record retention review')}
                    </button>
                  </div>
                ) : (
                  <div className="privacy-review-card__recorded">
                    <strong>{t('Retention review recorded')}</strong>
                    <p>{review.items[0]?.controller_legal_basis || t('No review note recorded.')}</p>
                    <span>
                      {t('Delete or review again by')}: {formatDate(review.items[0]?.retain_until)}
                    </span>
                    {retentionDue && (
                      <p className="privacy-review-card__deadline-warning">
                        {t('The recorded retention deadline has passed. Delete the records under your approved procedure, or document a renewed lawful basis and end date without delay.')}
                      </p>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
